import {Component, Plugins} from '@packages/systems/wfdl';
import {type Result} from '@packages/utilities/fp/result';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {getName, namesEqual, qualify} from '@packages/systems/wfdl-Qualified';
import {getAncestorComponents} from '@packages/systems/user-components/utils';
import {designerTranslation} from '@packages/systems/designer-translations';
import {
  buildForbiddenRichTextNestingError,
  findRichTextForbiddenElement,
} from '@packages/utilities/rich-text-validation';

const pageComponentType = qualify(
  Plugins.name('__SitePlugin'),
  Component.name('page')
);

/**
 * Validates that elements being added to a component used in CMS Rich Text
 * don't contain forbidden element types (Collection Lists).
 *
 * Walks the component nesting chain to determine if the currently focused
 * component (or any ancestor) has its root ID in the CMS RTF component IDs.
 */
export function validateCmsRtfNesting(params: {
  child: ExpressionElement;
  componentMap: Component.ComponentMap;
  currentComponentType: Plugins.QualifiedElement;
  cmsRtfComponentIds?: readonly string[];
}): Result<string, ExpressionElement> | undefined {
  const {child, componentMap, currentComponentType, cmsRtfComponentIds} =
    params;

  if (!cmsRtfComponentIds?.length) return undefined;
  if (namesEqual(currentComponentType, pageComponentType)) return undefined;

  const cmsIds = new Set(cmsRtfComponentIds);
  const currentRootId = String(getName(currentComponentType));

  let isUsedInCmsRtf = cmsIds.has(currentRootId);
  if (!isUsedInCmsRtf) {
    const ancestorIds = getAncestorComponents(componentMap)(currentRootId);
    isUsedInCmsRtf = ancestorIds.some((id) => cmsIds.has(id));
  }
  if (!isUsedInCmsRtf) return undefined;

  const forbiddenElement = findRichTextForbiddenElement(child, componentMap);
  if (!forbiddenElement) return undefined;

  const {t} = designerTranslation;
  return buildForbiddenRichTextNestingError({
    child,
    forbiddenElement,
    ancestorLabel: 'rich text',
    translate: t,
  });
}
